import React, { FC, use, useMemo, useState } from "react";
import { Card, Col, Row } from "antd";
import { FormattedMessage } from "react-intl";

import DebitorForm from "./DebitorForm";
import { CompanyContext } from "scripts/context/CompanyContext";
import { KontoContext, KontoControlContext } from "scripts/context/accountingData/KontoEntitiesProvider";
import { Debitor } from "scripts/models/Creditor";
import { FormHeader, getFiltered, SearchField } from "../helpers";
import { GlobalYearSelect } from "../../../../components/shared/Toolbar/YearSelect";
import { NumNameTable } from "../NumNameTable";
import { ProductKeys } from "scripts/models/Product";
import { useCanWrite } from "scripts/infrastructure/hooks/usePermissions";
import { RecordsContext } from "../../../../../scripts/context/accountingData/RecordsCtx";

const EditDebView: FC = () => {
    const canWrite = useCanWrite(ProductKeys.AccountingCommon);
    const { allRecords } = use(RecordsContext);
    const { debitors } = use(KontoContext);
    const { debSave, debDelete } = use(KontoControlContext);
    const { yearConfig } = use(CompanyContext);
    const kontoExt = yearConfig?.kontoExt ?? 0;

    const [activeItem, setActiveItem] = useState<Debitor | null>(null);
    const [querySearch, setQuerySearch] = useState("");

    const handleClear = React.useCallback(() => setActiveItem(null), []);

    const uniqueNums = useMemo(() => debitors.map(debitor => debitor.getExtNum(kontoExt)), [debitors, kontoExt]);

    const usedItems = useMemo(() => {
        const set = new Set<string>();
        allRecords.list.forEach(r => {
            if (r.debetor) {
                set.add(r.debetor.num);
            }
            r.items.forEach(i => {
                if (i.debetor) {
                    set.add(i.debetor.num);
                }
            });
        });
        return set;
    }, [allRecords]);

    const tableItems = useMemo(() => {
        return getFiltered(debitors, querySearch, kontoExt).map(entity => ({
            entity,
            isUsed: usedItems.has(entity.num),
        }));
    }, [usedItems, debitors, querySearch, kontoExt]);

    return (
        <div className="ManageAccount__container">
            <Row align="middle" gutter={24} style={{ marginTop: 16, marginBottom: 16 }}>
                <Col>
                    <GlobalYearSelect />
                </Col>
                <Col>
                    <SearchField onSearch={setQuerySearch} />
                </Col>
            </Row>

            <Row gutter={32} style={{ flexGrow: 1 }}>
                <Col span={16} xxl={18}>
                    <NumNameTable
                        items={tableItems}
                        onClick={item => setActiveItem(item as Debitor)}
                        onDeleteClick={item => debDelete(item.key, item.num)}
                        isRemovable={item => !usedItems.has(item.num)}
                    />
                </Col>
                <Col span={7} xxl={5}>
                    <Card>
                        <FormHeader
                            isEditing={!!activeItem?.key}
                            entityLabel={<FormattedMessage id="app.titles.debitor" />}
                        />
                        <DebitorForm
                            item={activeItem}
                            disabled={!canWrite}
                            uniqueNums={uniqueNums}
                            onSave={debSave}
                            onClear={handleClear}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};
export default React.memo(EditDebView);
